import React, { useState, useEffect } from 'react';
import {
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    CircularProgress
} from '@mui/material';
import SectionTitle from '../../../ui/sectionTitle';
import { SelectLocation } from '../../../common';
import { ValidDataService } from '../../../../service/validDataService';
import { toast } from 'react-toastify';

function ValidAccessoryBasicInfo({ formik, labels }) {
    const [suppliers, setSuppliers] = useState([]);
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);
    const [brands, setBrands] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(false);
    
    const updateLocation = (location) => {
        formik.setFieldValue('location', location);
    };

    // Load suppliers on mount
    useEffect(() => {
        loadSuppliers();
    }, []);

    // Load brands when supplier changes
    useEffect(() => {
        if (formik.values.supplierId) {
            loadBrands(formik.values.supplierId);
        } else {
            setBrands([]);
            formik.setFieldValue('brand', '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.supplierId]);

    const loadSuppliers = async () => {
        setLoadingSuppliers(true);
        try {
            const response = await ValidDataService.getAllSuppliers('accessories');
            if (response.code === 0 && response.data) {
                setSuppliers(response.data);
            }
        } catch (error) {
            console.error('Error loading suppliers:', error);
            toast.error('Failed to load suppliers');
        } finally {
            setLoadingSuppliers(false);
        }
    };

    const loadBrands = async (supplierId) => {
        setLoadingBrands(true);
        try {
            const response = await ValidDataService.getBrandsForSupplier(supplierId);
            if (response.code === 0 && response.data) {
                setBrands(response.data);
            }
        } catch (error) {
            console.error('Error loading brands:', error);
            toast.error('Failed to load brands');
        } finally {
            setLoadingBrands(false);
        }
    };

    const handleSupplierChange = (event, value) => {
        if (value) {
            formik.setFieldValue('supplierId', value.id);
            formik.setFieldValue('supplierName', value.supplierName);
        } else {
            formik.setFieldValue('supplierId', '');
            formik.setFieldValue('supplierName', '');
        }
        // Reset brand when supplier changes
        formik.setFieldValue('brand', '');
        formik.setFieldValue('brandId', '');
    };

    const handleBrandChange = (event, value) => {
        if (typeof value === 'string') {
            // User typed a new brand name
            formik.setFieldValue('brand', value);
            formik.setFieldValue('brandId', '');
        } else if (value && value.inputValue) {
            // User selected "Add new brand" option
            formik.setFieldValue('brand', value.inputValue);
            formik.setFieldValue('brandId', '');
        } else if (value) {
            // User selected an existing brand
            formik.setFieldValue('brand', value.name);
            formik.setFieldValue('brandId', value.id);
        } else {
            formik.setFieldValue('brand', '');
            formik.setFieldValue('brandId', '');
        }
    };

    return (
        <>
            <Grid item xs={12}>
                <SectionTitle title="Basic Information" />
            </Grid>

            {/* Supplier - Required */}
            <Grid item xs={12} sm={4} mt={1}>
                <Autocomplete
                    id="supplier"
                    options={suppliers}
                    getOptionLabel={(option) => option.supplierName || ''}
                    loading={loadingSuppliers}
                    value={suppliers.find(s => s.id === formik.values.supplierId) || null}
                    onChange={handleSupplierChange}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Supplier *"
                            size="small"
                            error={formik.touched.supplierId && Boolean(formik.errors.supplierId)}
                            helperText={formik.touched.supplierId && formik.errors.supplierId}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingSuppliers ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            </Grid>

            {/* Brand - Autocomplete with freeSolo for new entries */}
            <Grid item xs={12} sm={4} mt={1}>
                <Autocomplete
                    id="brand"
                    freeSolo
                    options={brands}
                    getOptionLabel={(option) => {
                        if (typeof option === 'string') {
                            return option;
                        }
                        if (option.inputValue) {
                            return option.inputValue;
                        }
                        return option.name || '';
                    }}
                    loading={loadingBrands}
                    value={
                        brands.find(b => b.id === formik.values.brandId) || formik.values.brand || ''
                    }
                    onChange={handleBrandChange}
                    disabled={!formik.values.supplierId}
                    filterOptions={(options, params) => {
                        const filtered = options.filter(option => 
                            option.name.toLowerCase().includes(params.inputValue.toLowerCase())
                        );

                        const { inputValue } = params;
                        // Suggest creating a new brand if input doesn't match any existing
                        const isExisting = options.some((option) => inputValue === option.name);
                        if (inputValue !== '' && !isExisting) {
                            filtered.push({
                                inputValue,
                                name: `Add "${inputValue}"`,
                            });
                        }

                        return filtered;
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Brand"
                            size="small"
                            error={formik.touched.brand && Boolean(formik.errors.brand)}
                            helperText={(formik.touched.brand && formik.errors.brand) || (!formik.values.supplierId ? 'Select supplier first' : '')}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingBrands ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            </Grid>

            {/* Model/Product Number */}
            <Grid item xs={12} sm={4} mt={1}>
                <TextField
                    id="modelNumber"
                    name="modelNumber"
                    label={labels.modelNumber || "Product/Model Number *"}
                    size="small"
                    fullWidth
                    value={formik.values.modelNumber}
                    onChange={formik.handleChange}
                    error={Boolean(formik.errors.modelNumber)}
                    helperText={formik.errors.modelNumber}
                />
            </Grid>

            {/* SKU */}
            <Grid item xs={12} sm={3} mt={1}>
                <TextField
                    id="sku"
                    name="sku"
                    label="SKU"
                    size="small"
                    fullWidth
                    onBlur={formik.handleBlur}
                    value={formik.values.sku}
                    onChange={formik.handleChange}
                    error={formik.touched.sku && Boolean(formik.errors.sku)}
                    helperText={formik.touched.sku && formik.errors.sku}
                />
            </Grid>

            {/* Internal UPC */}
            <Grid item xs={12} sm={2} mt={1}>
                <TextField
                    id="internalUpc"
                    name="internalUpc"
                    label="Internal UPC"
                    size="small"
                    fullWidth
                    onBlur={formik.handleBlur}
                    value={formik.values.internalUpc}
                    onChange={formik.handleChange}
                    error={formik.touched.internalUpc && Boolean(formik.errors.internalUpc)}
                    helperText={formik.touched.internalUpc && formik.errors.internalUpc}
                />
            </Grid>

            {/* Supplier UPC */}
            <Grid item xs={12} sm={2} mt={1}>
                <TextField
                    id="supplierUpc"
                    name="supplierUpc"
                    label="Supplier UPC"
                    size="small"
                    fullWidth
                    onBlur={formik.handleBlur}
                    value={formik.values.supplierUpc}
                    onChange={formik.handleChange}
                    error={formik.touched.supplierUpc && Boolean(formik.errors.supplierUpc)}
                    helperText={formik.touched.supplierUpc && formik.errors.supplierUpc}
                />
            </Grid>

            {/* Location */}
            <Grid item xs={12} sm={2} mt={1} mr={2}>
                <SelectLocation 
                    fullWidth={true} 
                    location={formik.values.location.id || ''} 
                    error={Boolean(formik.errors.location)} 
                    required={true}
                    helperText={formik.errors.location} 
                    setLocation={updateLocation}
                />
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={2} mt={1}>
                <FormControl fullWidth>
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select
                        id="status"
                        name="status"
                        label="Status"
                        size="small"
                        value={formik.values.status}
                        onChange={formik.handleChange}
                        error={formik.touched.status && Boolean(formik.errors.status)}
                    >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
        </>
    );
}

export default ValidAccessoryBasicInfo;
